import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button, Input, Popover, Spin } from 'antd';
import AvaIcon from 'assets/common/ava.png';
import loadingIcon from 'assets/common/ailoading.svg';
import { CloseOutlined } from '@ant-design/icons';
import { EAIRichTextEditorType } from 'types/ai';
import type { TConfigModule, TGetRichTextResultRes } from 'types/ai';
import { getRichTextAiResult } from 'api/ai';
import s from './s.module.less';
import { AdjustLengthIcon, ChangeToneIcon, TellAvaIcon } from 'components/MenuIcons';
import './TemplateVariableBlot';
import { variablesToBlotHtml, blotHtmlToVariables } from './templateVariableUtils';

type IProps = {
    onFetchingUpdate?: (isFetching: boolean) => void;
    onChange?: (val: string) => void;
    value?: string;
    conversionId?: string;
    resetFlag?: {}; //if !== undefined, will clear history
    clearMessageFlag?: {}; //if !== undefined, will clear message
    staticAIActions?: TConfigModule[];
    hideConfigActions?: boolean;
    forceEnabled?: boolean;
    isOps?: boolean;
    relationId: number;
    placeholder?: string;
    type?: EAIRichTextEditorType;
    loading?: boolean;
    height?: string;
    context?: Record<string, string>; // 如果 ai 生成需要其他参数
    disabled?: boolean; // 只读：禁止编辑器输入及工具栏/AI 操作
};

type THistory = {
    title: string;
    content: string;
};

type TAIRep = {
    messageId: string;
    conversionId: string;
    content: string;
};

const Buttons = [
    {
        label: 'Adjust length',
        icon: AdjustLengthIcon,
        tooltip: 'Adjust content length',
        options: [
            {
                label: 'Simplify',
                value: 'ADJUST_LENGTH_SIMPLIFY',
            },

            {
                label: 'Rephrase',
                value: 'ADJUST_LENGTH_REPHRASE',
            },
            {
                label: 'Elaborate',
                value: 'ADJUST_LENGTH_ELABORATE',
            },
        ],
    },
    {
        label: 'Change tone',
        icon: ChangeToneIcon,
        tooltip: 'Change writing tone',
        options: [
            {
                label: 'Casual',
                value: 'CHANGE_TONE_CASUAL',
            },
            {
                label: 'Friendly',
                value: 'CHANGE_TONE_FRIENDLY',
            },
            {
                label: 'Empathetic',
                value: 'CHANGE_TONE_EMPATHETIC',
            },
            {
                label: 'Formal',
                value: 'CHANGE_TONE_FORMAL',
            },
        ],
    },
    {
        label: 'Tell Ava to...',
        icon: TellAvaIcon,
        tooltip: 'Edit with an instruction',
        value: 'TELL_AVA_TO_SUBMIT',
        placeholder: 'For example: make it sounds joyful',
    },
];

/**
 * Imperatively set Quill editor content with template variable blots.
 * This bypasses ReactQuill's controlled value prop to avoid HTML mismatch issues.
 */
function setEditorContentWithBlots(quillRef: React.RefObject<ReactQuill>, html: string) {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const blotHtml = variablesToBlotHtml(html);
    editor.setContents([] as any);
    (editor as any).clipboard.dangerouslyPasteHTML(0, blotHtml);
}

/**
 * Read Quill editor content as plain HTML with [Variable] text (no blot spans).
 */
function getEditorPlainHtml(quillRef: React.RefObject<ReactQuill>): string {
    const editor = quillRef.current?.getEditor();
    if (!editor) return '';
    return blotHtmlToVariables(editor.root.innerHTML);
}

export default function AIRichText(props: IProps) {
    const {
        onFetchingUpdate,
        onChange,
        conversionId,
        clearMessageFlag,
        isOps,
        resetFlag,
        type = EAIRichTextEditorType.TEXT_VISIT_TREATMENT,
        value,
        forceEnabled,
        relationId,
        placeholder,
        loading,
        height = '250px',
        context,
        disabled = false,
    } = props;
    const quillRef = useRef<ReactQuill>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const [isFetching, setIsFetching] = useState(false);
    const [isLikeing, setIsLikeing] = useState(false);
    const [historyList, setHistoryList] = useState<THistory[]>([]);
    const [openInx, setOpenInx] = useState<number>();
    const [val, setVal] = useState(value || '');
    const [currentConversionId, setCurrentConversionId] = useState('');
    const [lastAiResultId, setLastAiResultId] = useState<string>();
    const [showHistory, setShowHistory] = useState(false);
    const [likeStatus, setLikeStatus] = useState<boolean>();
    const [showVote, setShowVote] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();
    const [aiPrompt, setAiPrompt] = useState<string>();
    const [, forceUpdate] = useState({});
    const aiRequestFlag = useRef<'normal' | 'canceled'>('normal');
    const aiApiRequestString = useRef<string>();
    const hasInitDefaultValueFlag = useRef(false);
    const hasHistory = !!(historyList && historyList.length > 0);
    const [editingVariable, setEditingVariable] = useState<{
        blotNode: HTMLElement;
        rect: DOMRect;
        variableName: string;
    } | null>(null);
    const [editInputValue, setEditInputValue] = useState('');
    // Suppress ReactQuill's onChange during imperative content updates
    const suppressOnChangeRef = useRef(true);

    const addToHistory = (item: THistory) => {
        const newHistoryList = [...historyList, item];
        setHistoryList(newHistoryList);
    };

    const handleCancelRequest = () => {
        aiRequestFlag.current = 'canceled';
        setIsFetching(false);
    };

    const triggerAiAction = async (rewriteAction: string) => {
        if (!val) {
            return;
        }
        const requestData: TGetRichTextResultRes = {
            rewriteAction,
            type,
            content: blotHtmlToVariables(val),
            prompt: rewriteAction === 'TELL_AVA_TO_SUBMIT' ? aiPrompt : undefined,
            relationId,
            format: 'HTML',
            context,
        };

        aiApiRequestString.current = JSON.stringify(requestData);
        setLikeStatus(undefined);
        setIsFetching(true);
        setShowVote(false);

        const result = await getRichTextAiResult(requestData);
        if (!result.error) {
            if (aiApiRequestString.current === JSON.stringify(requestData)) {
                const resultData: TAIRep = result.data?.data;
                console.log('resultData', resultData.content, typeof resultData.content);
                // Set content imperatively with blots
                suppressOnChangeRef.current = true;
                setEditorContentWithBlots(quillRef, resultData.content);
                suppressOnChangeRef.current = false;
                setVal(resultData.content);
                // 通知父组件值已变化（触发自动保存等），否则父状态停留在旧值
                onChange?.(resultData.content);
                setLastAiResultId(resultData.messageId);
                setCurrentConversionId(resultData.conversionId);
                if (!isOps) {
                    setShowVote(true);
                }
                addToHistory({
                    title: rewriteAction,
                    content: resultData.content!,
                });
            }
        }
        setIsFetching(false);
    };

    const handleChange = (content: string) => {
        if (suppressOnChangeRef.current) return;
        // Convert blot HTML back to [Variable] format before notifying parent
        const plainContent = blotHtmlToVariables(content);
        if (plainContent !== value) {
            setVal(plainContent);
            onChange?.(plainContent);
        }
    };

    React.useEffect(() => {
        onFetchingUpdate?.(isFetching);
    }, [isFetching]);

    React.useEffect(() => {
        if (clearMessageFlag) {
            onChange?.('');
            setVal('');
        }
    }, [clearMessageFlag]);

    // When value prop changes from parent, imperatively update editor with blots
    React.useEffect(() => {
        if (val !== value) {
            const newVal = value || '';
            setVal(newVal);
            suppressOnChangeRef.current = true;
            setEditorContentWithBlots(quillRef, newVal);
            suppressOnChangeRef.current = false;
        }
    }, [value]);

    React.useEffect(() => {
        if (resetFlag) {
            setHistoryList([]);
            aiRequestFlag.current = 'normal';
            aiApiRequestString.current = undefined;
            setShowVote(false);
            setLikeStatus(undefined);
            setLastAiResultId(undefined);
            setCurrentConversionId('');
            setOpenInx(undefined);
            setAiPrompt('');
            hasInitDefaultValueFlag.current = false;
        }
    }, [resetFlag]);

    React.useEffect(() => {
        if (hasInitDefaultValueFlag.current) {
            return;
        }
        if (value && !val) {
            hasInitDefaultValueFlag.current = true;
            setVal(value);
            suppressOnChangeRef.current = true;
            setEditorContentWithBlots(quillRef, value);
            suppressOnChangeRef.current = false;
        }
    }, [value, val]);

    React.useEffect(() => {
        if (conversionId) {
            setCurrentConversionId(conversionId);
        }
    }, [conversionId]);

    // When height/layout changes (e.g. open in full-screen modal),
    // Quill may keep the previous scroll position, causing "blank" views.
    // Reset editor scroll to top for a consistent UX.
    React.useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        if (!editor.root) return;
        editor.root.scrollTop = 0;

        // Quill 在弹窗/布局变化时有概率尺寸计算不及时，
        // 显式 update 一次并在下一帧再刷新，避免出现高度不贴合的问题。
        try {
            editor.update?.();
        } catch {
            // ignore
        }
        requestAnimationFrame(() => {
            try {
                editor.update?.();
            } catch {
                // ignore
            }
        });
    }, [height]);

    React.useEffect(() => {
        return () => {
            if (timeoutRef?.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Inject blots on initial mount; suppressOnChangeRef starts as true
    // so any onChange fired by ReactQuill during first render is ignored.
    useEffect(() => {
        if (value) {
            setEditorContentWithBlots(quillRef, value);
        }
        suppressOnChangeRef.current = false;
    }, []);

    // Click handler using event delegation on wrapper div
    // This avoids timing issues with Quill editor initialization
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const handleClick = (e: MouseEvent) => {
            const target = (e.target as HTMLElement).closest('.ql-template-variable') as HTMLElement | null;
            if (target) {
                const variableName = target.getAttribute('data-variable') || '';
                const rect = target.getBoundingClientRect();
                setEditingVariable({ blotNode: target, rect, variableName });
                setEditInputValue('');
                e.preventDefault();
                e.stopPropagation();
            }
        };
        wrap.addEventListener('click', handleClick);
        return () => {
            wrap.removeEventListener('click', handleClick);
        };
    }, []);

    // Close popover on outside click
    useEffect(() => {
        if (!editingVariable) return;
        const handleOutsideClick = (e: MouseEvent) => {
            const popover = document.getElementById('variable-edit-popover');
            if (popover && !popover.contains(e.target as Node)) {
                setEditingVariable(null);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [editingVariable]);

    const handleVariableConfirm = useCallback(() => {
        if (!editingVariable || !editInputValue.trim()) return;
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const blot = (ReactQuill.Quill as any).find(editingVariable.blotNode);
        if (blot) {
            const index = editor.getIndex(blot);
            editor.deleteText(index, 1);
            editor.insertText(index, editInputValue.trim());
            // Sync val state with the updated content
            const newPlainContent = getEditorPlainHtml(quillRef);
            setVal(newPlainContent);
            onChange?.(newPlainContent);
        }
        setEditingVariable(null);
        setEditInputValue('');
    }, [editingVariable, editInputValue, onChange]);

    const HistoryContent = () => {
        return (
            <div className={s.historyWrapPop}>
                <div className={s.historyTitle}>Choose a version from the history</div>
                <div className={s.historyContent}>
                    {historyList?.map((item: THistory, inx) => {
                        return (
                            <div className={s.historyItem} key={`${item.title}_${inx}`}>
                                <div className={s.historyItemTitle}>{item.title}</div>
                                <div className={s.historyItemContent}>{item.content}</div>
                                <div className={s.historyItemBtn}>
                                    <Button
                                        onClick={() => {
                                            suppressOnChangeRef.current = true;
                                            setEditorContentWithBlots(quillRef, item.content);
                                            suppressOnChangeRef.current = false;
                                            setVal(item.content);
                                            onChange?.(item.content);
                                            setShowHistory(false);
                                        }}
                                        className={s.btn}
                                        size="small"
                                    >
                                        Select
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const formats = useMemo(
        () => [
            'header',
            'size',
            'bold',
            'italic',
            'underline',
            'strike',
            'list',
            'bullet',
            'indent',
            'link',
            'align',
            'template-variable',
        ],
        [],
    );

    const modules = useMemo(() => {
        return {
            toolbar: {
                container: [
                    ['undo', 'redo'],
                    [{ size: ['small', false, 'large', 'huge'] }],
                    ['bold', 'italic'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ align: [] }],
                    ['link'],
                ],
                handlers: {
                    undo: () => {
                        const editor = quillRef.current?.getEditor();
                        if (editor) {
                            (editor as any).history.undo();
                        }
                    },
                    redo: () => {
                        const editor = quillRef.current?.getEditor();
                        if (editor) {
                            (editor as any).history.redo();
                        }
                    },
                },
            },
            history: {
                delay: 1000,
                maxStack: 50,
            },
        };
    }, []);

    return (
        <div className={`${s.wrap} ${disabled ? s.wrapDisabled : ''}`} ref={wrapRef} style={{ height }}>
            {loading && <div className={s.generating}>Generating...</div>}
            <ReactQuill
                ref={quillRef}
                defaultValue={value || ''}
                onChange={handleChange}
                placeholder={placeholder}
                className={s.editor}
                modules={modules}
                formats={formats}
                readOnly={disabled}
            />

            <div className={s.actions}>
                <div className={s.left}>
                    <img className={s.ava} src={AvaIcon} />
                    {isFetching && (
                        <div className={s.workingWrap}>
                            <img className={s.loading} src={loadingIcon} />
                            <span>Working on it</span>
                            <span onClick={handleCancelRequest} className={s.cancel}>
                                <CloseOutlined />
                            </span>
                        </div>
                    )}
                    {!isFetching && (
                        <div
                            className={
                                forceEnabled
                                    ? s.configWrap
                                    : !val
                                      ? `${s.configWrap} ${s.configWrapDisabled}`
                                      : s.configWrap
                            }
                        >
                            {Buttons.map((item, inx) => {
                                const content = item.options ? (
                                    <div className={s.configAction}>
                                        {item.options.map((option) => {
                                            return (
                                                <div
                                                    key={option.value}
                                                    onClick={() => {
                                                        triggerAiAction(option.value);
                                                    }}
                                                    className={s.configActionItem}
                                                >
                                                    {option.label}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className={s.configTextAction} key={item.label}>
                                        <div className={s.configTextTitle}>{item.label}</div>
                                        <div className={s.configTextInputWrap}>
                                            <Input.TextArea
                                                rows={5}
                                                placeholder={item.placeholder}
                                                value={aiPrompt}
                                                onChange={(e) => {
                                                    setAiPrompt(e.target.value);
                                                }}
                                            />
                                        </div>
                                        <div className={s.configTextSubmit}>
                                            <Button
                                                onClick={() => {
                                                    triggerAiAction(item.value);
                                                }}
                                                disabled={!aiPrompt}
                                                type="primary"
                                            >
                                                Submit
                                            </Button>
                                        </div>
                                    </div>
                                );
                                return (
                                    <div
                                        key={item.label}
                                        className={
                                            openInx === inx ? `${s.configItem} ${s.configItemActive}` : s.configItem
                                        }
                                    >
                                        <Popover
                                            trigger="click"
                                            arrow={false}
                                            placement={inx === 0 ? 'bottomLeft' : 'bottomLeft'}
                                            title=""
                                            open={openInx === inx && !(forceEnabled ? false : !val)}
                                            onOpenChange={(isOpen: boolean) => {
                                                const disableAction = forceEnabled ? false : !val;
                                                if (disableAction) {
                                                    return;
                                                }
                                                if (isOpen) {
                                                    setOpenInx(inx);
                                                } else {
                                                    setOpenInx(undefined);
                                                }
                                            }}
                                            content={content}
                                            rootClassName={s.pop}
                                        >
                                            <Popover
                                                trigger="hover"
                                                arrow={false}
                                                title=""
                                                content={<div className={s.hoverTips}>{item.tooltip}</div>}
                                                placement={inx === 0 ? 'bottomLeft' : 'bottomLeft'}
                                                rootClassName={s.pop}
                                            >
                                                <div className={s.configItemInner}>
                                                    {item.icon}
                                                    <span className={s.configName}>{item.label}</span>
                                                </div>
                                            </Popover>
                                        </Popover>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                {!isFetching && hasHistory && (
                    <div className={s.right}>
                        <Popover
                            trigger="click"
                            open={showHistory}
                            onOpenChange={(v) => {
                                setShowHistory(v);
                            }}
                            arrow={false}
                            placement="topRight"
                            title=""
                            content={<HistoryContent />}
                        >
                            <div className={s.historyWrap}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                >
                                    <g clipPath="url(#clip0_3081_10987)">
                                        <path
                                            d="M8.66667 4C8.48986 4 8.32029 4.07024 8.19526 4.19526C8.07024 4.32029 8 4.48986 8 4.66667V8.33333C8 8.51014 8.07024 8.67971 8.19526 8.80474C8.32029 8.92976 8.48986 9 8.66667 9H12C12.1768 9 12.3464 8.92976 12.4714 8.80474C12.5964 8.67971 12.6667 8.51014 12.6667 8.33333C12.6667 8.15652 12.5964 7.98695 12.4714 7.86193C12.3464 7.7369 12.1768 7.66667 12 7.66667H9.47333C9.42913 7.66667 9.38674 7.64911 9.35548 7.61785C9.32423 7.5866 9.30667 7.5442 9.30667 7.5V4.66667C9.3068 4.49438 9.24024 4.32873 9.12092 4.20445C9.00161 4.08016 8.83882 4.00689 8.66667 4Z"
                                            fill="#6B7280"
                                        />
                                        <path
                                            d="M8.3801 0.839636C6.94581 0.895524 5.56088 1.37844 4.40282 2.2265C3.24476 3.07456 2.36632 4.24912 1.8801 5.59964C1.86679 5.6353 1.84167 5.66533 1.80892 5.68473C1.77618 5.70414 1.73777 5.71175 1.7001 5.7063L0.573435 5.53964C0.511419 5.53132 0.448308 5.53994 0.390796 5.56459C0.333284 5.58924 0.283515 5.62899 0.246768 5.67964C0.21439 5.73288 0.197266 5.79399 0.197266 5.8563C0.197266 5.91861 0.21439 5.97973 0.246768 6.03297L2.0001 9.1663C2.02785 9.2135 2.06658 9.2533 2.11301 9.28232C2.15944 9.31134 2.21218 9.32871 2.26677 9.33297C2.31741 9.33852 2.36865 9.33305 2.41698 9.31694C2.46531 9.30083 2.50958 9.27446 2.54677 9.23964L5.0601 6.73964C5.10412 6.69602 5.13512 6.641 5.1496 6.58075C5.16408 6.5205 5.16149 6.4574 5.1421 6.39855C5.12271 6.33969 5.0873 6.2874 5.03984 6.24756C4.99238 6.20771 4.93476 6.18188 4.87343 6.17297L3.72677 5.99964C3.70161 5.99495 3.6778 5.98474 3.65705 5.96976C3.6363 5.95477 3.61913 5.93538 3.60677 5.91297C3.59507 5.89029 3.58897 5.86515 3.58897 5.83964C3.58897 5.81412 3.59507 5.78898 3.60677 5.7663C4.00375 4.86709 4.63418 4.09053 5.43261 3.51724C6.23105 2.94394 7.16838 2.59481 8.14731 2.50608C9.12623 2.41736 10.1111 2.59228 10.9996 3.01268C11.8881 3.43309 12.6478 4.08365 13.2 4.89683C13.7522 5.71001 14.0766 6.65617 14.1397 7.63709C14.2027 8.618 14.0021 9.59791 13.5585 10.4751C13.115 11.3523 12.4447 12.0947 11.6174 12.6254C10.79 13.1561 9.83566 13.4556 8.85343 13.493C8.63242 13.5009 8.42362 13.5964 8.27297 13.7583C8.12231 13.9202 8.04214 14.1353 8.0501 14.3563C8.05806 14.5773 8.15349 14.7861 8.31539 14.9368C8.4773 15.0874 8.69242 15.1676 8.91343 15.1596C10.8133 15.0898 12.6075 14.2681 13.9015 12.8753C15.1955 11.4826 15.8833 9.6328 15.8134 7.73297C15.7436 5.83313 14.9219 4.03886 13.5291 2.74486C12.1364 1.45086 10.2866 0.763129 8.38677 0.832969L8.3801 0.839636Z"
                                            fill="currentColor"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3081_10987">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <span className={s.view}>View history</span>
                            </div>
                        </Popover>
                    </div>
                )}
            </div>

            {editingVariable && (
                <div
                    id="variable-edit-popover"
                    className={s.variablePopover}
                    style={{
                        position: 'fixed',
                        top: editingVariable.rect.bottom + 4,
                        left: editingVariable.rect.left,
                    }}
                >
                    <div className={s.variablePopoverTitle}>
                        Replace <strong>[{editingVariable.variableName}]</strong>
                    </div>
                    <Input
                        autoFocus
                        placeholder={`Enter ${editingVariable.variableName}`}
                        value={editInputValue}
                        onChange={(e) => setEditInputValue(e.target.value)}
                        onPressEnter={handleVariableConfirm}
                    />
                    <div className={s.variablePopoverActions}>
                        <Button size="small" onClick={() => setEditingVariable(null)}>
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            type="primary"
                            disabled={!editInputValue.trim()}
                            onClick={handleVariableConfirm}
                            style={{ background: '#0E62F4' }}
                        >
                            Confirm
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
